import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  numeric,
  varchar,
  jsonb,
  index,
  date,
  uniqueIndex,
  check,
  pgEnum,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { normalizeStateToCode } from "../utils/normalizeState";

// Canonicalizes any US state input to a 2-letter code at the validation layer,
// so every API write (insert/update) stores a consistent value. nullish() keeps
// the field optional+nullable; absent keys on .partial() stay untouched.
const stateCodeSchema = z
  .string()
  .nullish()
  .transform((v) => normalizeStateToCode(v ?? null));
import { SIC_CODES } from "../sicCodes";
import { commissionSplitProfiles } from "./financial";

// Status enums — single source of truth at DB level
export const clientStatusEnum = pgEnum("client_status", ["active", "pending", "inactive"]);
export const prospectStatusEnum = pgEnum("prospect_status", ["lead", "qualified", "proposal_sent", "negotiating", "closed_won", "closed_lost"]);
export const employeeStatusEnum = pgEnum("employee_status", ["active", "inactive", "terminated", "cobra", "leave-of-absence", "retired", "pending_activation"]);

// Agencies: top-level organization per broker
export const agencies = pgTable("agencies", {
  id: varchar("id").primaryKey(), // UUID (usually the broker user's UUID)
  name: text("name").notNull(),
  contactEmail: text("contact_email"),
  phone: varchar("phone", { length: 20 }),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  subscriptionTier: varchar("subscription_tier", { length: 20 }).default("standard"),
  createdAt: timestamp("created_at").defaultNow(),
});


// Carrier Type Enum
export enum CarrierType {
  HEALTH = "health",
  MEDICAL = "medical", // Treated as equivalent to HEALTH
  DENTAL = "dental",
  VISION = "vision",
  LIFE = "life",
  STD = "std",
  LTD = "ltd",
  ACCIDENT = "accident",
  CRITICAL_ILLNESS = "critical_illness",
  CANCER = "cancer",
  HOSPITAL_INDEMNITY = "hospital_indemnity",
  TELEHEALTH = "telehealth",
  LEGAL = "legal",
  ID_THEFT = "id_theft",
  FSA_HEALTH = "fsa_health",
  FSA_DEPENDENT_CARE = "fsa_dependent_care",
}

export const CARRIER_TYPES = Object.values(CarrierType);

// Reference data: Standard Industrial Classification (SIC) codes
export const sicCodes = pgTable("sic_codes", {
  id: serial("id").primaryKey(),
  code: integer("code").notNull().unique(),
  office: text("office").notNull(),
  industryTitle: text("industry_title").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const clients = pgTable("clients", {
  id: serial("id").primaryKey(),
  // Agency scoping: the broker/agency UUID that owns this client
  agencyId: varchar("agency_id"),
  name: text("name").notNull(),
  primaryContactName: text("primary_contact_name"),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  website: text("website"),
  address: text("address"),
  // New normalized address fields
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  status: clientStatusEnum("status").notNull().default("active"),
  ein: text("ein"), // Employer Identification Number
  groupNumber: text("group_number"), // Insurance group number assigned by carrier
  sic: text("sic"), // Legacy free-text SIC. Deprecated - use sicCodeId.
  sicCodeId: integer("sic_code_id"), // SIC code number - validated against static SIC_CODES array
  entityType: text("entity_type"), // Corporation, C Corporation, S Corporation, LLC, LLP, Partnership, Sole Proprietor, Non-Profit, Government Agency
  primaryContactEmployeeId: integer("primary_contact_employee_id"),
  // Agency representatives/contacts
  primaryProducerId: varchar("primary_producer_id"), // references users.id (agency_member)
  secondaryProducerId: varchar("secondary_producer_id"), // references users.id (agency_member)
  accountManagerId: varchar("account_manager_id"), // references users.id (agency_member)
  accountExecutiveId: varchar("account_executive_id"), // references users.id (agency_member)
  teamId: integer("team_id"), // references agency_teams.id - team assigned to this client
  logo: text("logo"), // base64 encoded image or file path
  clientSince: timestamp("client_since"), // Manual entry or auto-set on prospect conversion
  // Client-level commission settings
  agencyAgentSplitId: integer("agency_agent_split_id").references(() => commissionSplitProfiles.id, { onDelete: "set null" }),
  aleStatus: boolean("ale_status"),
  aleStatusDeterminedAt: timestamp("ale_status_determined_at"),
  aleStatusCalculationYear: integer("ale_status_calculation_year"),
  cobraStatus: boolean("cobra_status"),
  cobraStatusDeterminedAt: timestamp("cobra_status_determined_at"),
  cobraStatusCalculationYear: integer("cobra_status_calculation_year"),
  // Renewal pipeline stage (broker Renewals page); values in RENEWAL_STAGE_KEYS
  renewalStage: text("renewal_stage").notNull().default("not_started"),
  // Stamped by storage.updateClient on stage changes; the daily reset job uses
  // it to spare stages set for the new cycle (resetExpiredRenewalStages)
  renewalStageUpdatedAt: timestamp("renewal_stage_updated_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const RENEWAL_STAGE_KEYS = [
  "not_started",
  "rfp_out",
  "proposals_in",
  "presented",
  "uw_review",
  "open_enrollment",
  "complete",
] as const;

// Superadmin-authored notes on a client record. Append-only history with
// editable bodies (creator can revise). Surfaced on the Client Record Notes tab.
export const clientNotes = pgTable(
  "client_notes",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    title: varchar("title", { length: 200 }),
    body: text("body").notNull(),
    createdBy: varchar("created_by", { length: 255 }).notNull(), // users.id
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    clientIdIdx: index("idx_client_notes_client_id").on(t.clientId),
    // For the paginated list query (most recent first)
    clientCreatedIdx: index("idx_client_notes_client_created").on(t.clientId, t.createdAt),
  })
);

export const insertClientNoteSchema = createInsertSchema(clientNotes)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    title: z.string().max(200).optional().nullable(),
    body: z.string().min(1, "Note body is required"),
  });

export type ClientNote = typeof clientNotes.$inferSelect;
export type InsertClientNote = z.infer<typeof insertClientNoteSchema>;

export const prospects = pgTable("prospects", {
  id: serial("id").primaryKey(),
  // Agency scoping: the broker/agency UUID that owns this prospect
  agencyId: varchar("agency_id"),
  name: text("name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone"),
  address: text("address"),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  status: prospectStatusEnum("status").notNull().default("lead"),
  source: text("source"), // referral, website, cold_call, marketing
  priority: text("priority").notNull().default("medium"), // high, medium, low
  notes: text("notes"),
  expectedValue: decimal("expected_value", { precision: 10, scale: 2 }),
  expectedCloseDate: timestamp("expected_close_date"),
  lastContactDate: timestamp("last_contact_date"),
  clientId: integer("client_id").references(() => clients.id), // set when converted to client
  // Agency representatives/contacts
  primaryProducerId: varchar("primary_producer_id"), // references users.id (agency_member)
  secondaryProducerId: varchar("secondary_producer_id"), // references users.id (agency_member)
  accountManagerId: varchar("account_manager_id"), // references users.id (agency_member)
  accountExecutiveId: varchar("account_executive_id"), // references users.id (agency_member)
  teamId: integer("team_id"), // references agency_teams.id - team assigned to this prospect
  logo: text("logo"), // S3 key or URL to logo image
  // Client-matching fields to support prospect-to-client conversion
  ein: varchar("ein", { length: 20 }),
  website: text("website"),
  primaryContactName: varchar("primary_contact_name", { length: 255 }),
  entityType: varchar("entity_type", { length: 100 }),
  sicCodeId: integer("sic_code_id"),
  sic: text("sic"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Junction table for agency representatives on clients
export const clientAccountManagers = pgTable("client_account_managers", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // references users.id (agency_member)
  agencyId: varchar("agency_id"), // references agencies.id (broker/agency UUID)
  role: varchar("role"), // e.g., "primary_producer", "secondary_producer", "account_manager", "account_executive", "additional_manager"
  agencyAgentSplitId: integer("agency_agent_split_id"), // references commission_split_profiles.id
  effectiveDate: date("effective_date").notNull().defaultNow(),
  endDate: date("end_date"),
  primarySecondarySplitId: integer("primary_secondary_split_id"), // references commission_split_profiles.id
  createdAt: timestamp("created_at").defaultNow(),
});

// Junction table for agency representatives on prospects
export const prospectAccountManagers = pgTable("prospect_account_managers", {
  id: serial("id").primaryKey(),
  prospectId: integer("prospect_id").notNull().references(() => prospects.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // references users.id (agency_member)
  agencyId: varchar("agency_id"), // references agencies.id (broker/agency UUID)
  role: varchar("role"), // e.g., "primary_producer", "secondary_producer", "account_manager", "account_executive", "additional_manager"
  effectiveDate: date("effective_date").notNull().defaultNow(),
  endDate: date("end_date"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const roles = pgTable("roles", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id"), // Scope roles to agencies
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  permissions: jsonb("permissions").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const employees = pgTable("employees", {
  id: serial("id").primaryKey(),
  roleId: integer("role_id").references(() => roles.id),
  // When an employee is created under the agency section, tie it to the broker user UUID
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"), // references clients.id
  prospectId: integer("prospect_id"), // references prospects.id
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  middleInitial: varchar("middle_initial", { length: 5 }),
  middleName: varchar("middle_name", { length: 100 }),
  formerName: varchar("former_name", { length: 255 }), // Former/Maiden name
  preferredLanguage: varchar("preferred_language", { length: 50 }), // English, Spanish, etc.
  email: varchar("email", { length: 255 }).notNull(), // Work email
  workPhone: varchar("work_phone", { length: 20 }), // Work phone
  personalEmail: varchar("personal_email", { length: 255 }), // Personal email
  phone: varchar("phone", { length: 20 }), // Personal phone
  dateOfBirth: date("date_of_birth"),
  sex: varchar("sex", { length: 10 }), // Male, Female, Other
  maritalStatus: varchar("marital_status", { length: 50 }), // Single, Married, Divorced, Domestic Partner, Legally Separated, Widowed
  ssn: varchar("ssn", { length: 11 }), // encrypted/masked
  tobaccoUser: varchar("tobacco_user", { length: 20 }), // Yes, No, Cessation Program
  // Tobacco surcharge fields (spec scenarios 03/18). tobaccoUser above is the
  // attested FACT and is never overwritten by cessation-program status; the
  // surcharge applies while tobaccoUser = Yes AND rasStatus = 'none'. Legacy
  // value tobaccoUser = "Cessation Program" reads as Yes + rasStatus enrolled.
  rasStatus: varchar("ras_status", { length: 20 }).default("none"), // none, enrolled, completed
  rasProgramName: varchar("ras_program_name", { length: 255 }),
  tobaccoAttestedAt: timestamp("tobacco_attested_at"),
  // When the RAS (cessation program) offer was disclosed in the enrollment
  // flow (scenarios 03/18) — the surcharge presumes the alternative was offered.
  rasDisclosedAt: timestamp("ras_disclosed_at"),
  disabled: varchar("disabled", { length: 10 }), // Yes, No
  hireDate: date("hire_date"),
  fullTimeDate: date("full_time_date"), // Date employee became full-time (may differ from hire date)
  department: varchar("department", { length: 100 }),
  position: varchar("position", { length: 100 }),
  employmentType: varchar("employment_type", { length: 50 }), // Full-Time, Part-Time, Variable-Hour, Seasonal, Contractor, Intern
  employeeId: varchar("employee_id", { length: 50 }),
  scheduledHours: varchar("scheduled_hours", { length: 20 }), // per week
  compensationType: varchar("compensation_type", { length: 20 }), // salary, hourly
  compensationAmount: varchar("compensation_amount", { length: 50 }),
  payCycle: varchar("pay_cycle", { length: 50 }), // Weekly, Bi-Weekly, Semi-Monthly, Monthly, Annually
  // When the current compensation_* values took effect. Set by the UI/API on
  // a raise; the employees trigger (migration 0129) writes the history row.
  compensationEffectiveDate: date("compensation_effective_date"),
  payGroupId: integer("pay_group_id"), // implicit FK -> payroll_settings.id (pay group); null = client default group
  salary: varchar("salary", { length: 50 }), // legacy field for backward compatibility
  status: employeeStatusEnum("status").notNull().default("active"),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }), // Apt, Suite, Unit, etc.
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  mailingAddress1: varchar("mailing_address1", { length: 255 }),
  mailingAddress2: varchar("mailing_address2", { length: 255 }),
  mailingCity: varchar("mailing_city", { length: 100 }),
  mailingState: varchar("mailing_state", { length: 50 }),
  mailingZipCode: varchar("mailing_zip_code", { length: 20 }),
  mailingCounty: varchar("mailing_county", { length: 100 }),
  emergencyContact: varchar("emergency_contact", { length: 255 }),
  emergencyPhone: varchar("emergency_phone", { length: 20 }),
  profilePicture: text("profile_picture"), // base64 encoded image or file path
  performanceRating: text("performance_rating"),
  lastReviewDate: date("last_review_date"),
  skills: text("skills").array(),
  managerEmployeeId: integer("manager_employee_id"),
  isManager: boolean("is_manager").default(false),
  terminationDate: date("termination_date"),
  terminationReason: varchar("termination_reason", { length: 100 }),
  terminationNotes: text("termination_notes"),
  // Contractor specific fields (nullable for regular employees)
  contractType: varchar("contract_type", { length: 50 }),
  contractStartDate: date("contract_start_date"),
  contractEndDate: date("contract_end_date"),
  hourlyRate: varchar("hourly_rate", { length: 20 }),
  taxId: varchar("tax_id", { length: 30 }),
  w9Status: varchar("w9_status", { length: 20 }),
  contractorNotes: text("contractor_notes"),
  // Banking information
  bankName: varchar("bank_name", { length: 255 }),
  routingNumber: varchar("routing_number", { length: 9 }),
  accountNumber: varchar("account_number", { length: 50 }),
  accountType: varchar("account_type", { length: 20 }).default("checking"),
  bankAccountVerified: boolean("bank_account_verified").default(false),
  // Detailed tax information
  filingStatus: varchar("filing_status", { length: 50 }),
  taxAllowances: integer("tax_allowances").default(0),
  additionalTaxWithholding: decimal("additional_tax_withholding", { precision: 10, scale: 2 }).default("0.00"),
  taxExempt: boolean("tax_exempt").default(false),
  // Organization references (nullable; scoped by owner)
  departmentId: integer("department_id"),
  jobTitleId: integer("job_title_id"),
  locationId: integer("location_id"),
  employeeClassId: integer("employee_class_id"),
  divisionId: integer("division_id"),
  // Agent license number (for agency employees only - required for regulatory compliance)
  licenseNumber: varchar("license_number", { length: 50 }),
  onboardingCompletedAt: timestamp("onboarding_completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  chkScope: check("chk_employees_scope", sql`(
    (CASE WHEN ${t.agencyId} IS NOT NULL THEN 1 ELSE 0 END) +
    (CASE WHEN ${t.clientId} IS NOT NULL THEN 1 ELSE 0 END) +
    (CASE WHEN ${t.prospectId} IS NOT NULL THEN 1 ELSE 0 END)
  ) <= 1`),
  // ONB-14: index for "X of Y completed onboarding" aggregates + the
  // employer dashboard filter on incomplete onboarding.
  onboardingCompletedAtIdx: index("employees_onboarding_completed_at_idx").on(
    t.onboardingCompletedAt,
  ),
}));

export const employeeDefaultValuesSchema = z.object({
  payCycle: z.string().optional(),
  employmentType: z.string().optional(),
  compensationType: z.string().optional(),
  scheduledHours: z.string().optional(),
  compensationAmount: z.string().optional(),
  status: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  county: z.string().optional(),
  country: z.string().optional(),
});

export type EmployeeDefaultValues = z.infer<typeof employeeDefaultValuesSchema>;

export const employeeDefaultSettings = pgTable(
  "employee_default_settings",
  {
    id: serial("id").primaryKey(),
    ownerType: varchar("owner_type", { length: 20 }).notNull(),
    ownerId: varchar("owner_id", { length: 64 }).notNull(),
    defaults: jsonb("defaults")
      .$type<EmployeeDefaultValues>()
      .notNull()
      .default(sql`'{}'::jsonb`),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    ownerIdx: uniqueIndex("employee_default_settings_owner_idx").on(
      table.ownerType,
      table.ownerId
    ),
  })
);

export const dependents = pgTable("dependents", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(), // references employees.id
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  middleInitial: varchar("middle_initial", { length: 5 }),
  formerName: varchar("former_name", { length: 255 }), // Former/Maiden name
  relationship: varchar("relationship", { length: 50 }).notNull(), // spouse, domestic_partner, child (future: child_adopted, child_guardian, etc.)
  dateOfBirth: timestamp("date_of_birth"),
  ssn: varchar("ssn", { length: 11 }), // encrypted/masked
  ssnNotAvailable: boolean("ssn_not_available").default(false), // bypass for when SSN is not currently available
  ssnFollowUpDate: date("ssn_follow_up_date"), // date to follow up for SSN (default 2 weeks from creation)
  sex: varchar("sex", { length: 10 }).notNull(), // male, female
  hasDifferentAddress: boolean("has_different_address").default(false), // if true, use address fields below
  address: text("address"), // if different from employee (legacy field)
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  isEligible: boolean("is_eligible").default(true),
  isDisabled: boolean("is_disabled").default(false), // disabled dependent — may stay on plan past age 26, exempt from age-out
  createdAt: timestamp("created_at").defaultNow(),
});

export const emergencyContacts = pgTable("emergency_contacts", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(), // references employees.id
  name: varchar("name", { length: 255 }).notNull(),
  relationship: varchar("relationship", { length: 100 }).notNull(),
  phone: varchar("phone", { length: 20 }).notNull(),
  email: varchar("email", { length: 255 }),
  address: text("address"),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  contactType: varchar("contact_type", { length: 50 }).default("Primary"),
  isPrimary: boolean("is_primary").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

// Employee History/Audit Log
export const employeeHistory = pgTable("employee_history", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(), // references employees.id
  action: text("action").notNull(), // profile_updated, employment_changed, enrollment_added, enrollment_cancelled, dependent_added, etc.
  description: text("description"),
  changes: text("changes"), // JSON string of field changes
  changedBy: text("changed_by").notNull(), // "Broker: James Duncan", "Employer Admin", "Employee", etc.
  changedByType: text("changed_by_type").notNull(), // broker, employer, employee, system
  changedByUserId: text("changed_by_user_id"), // user ID if available
  timestamp: timestamp("timestamp").defaultNow().notNull(),
  recordType: text("record_type"), // employee, dependent, emergency_contact, enrollment, etc.
  recordId: integer("record_id"), // ID of the related record if applicable
});

// Agency Benefits - Internal benefits for agency employees
export const agencyBenefits = pgTable("agency_benefits", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull(), // health, retirement, life, pto, dental, vision, etc.
  coverage: text("coverage").notNull(),
  cost: numeric("cost", { precision: 10, scale: 2 }).notNull(),
  participation: numeric("participation", { precision: 5, scale: 2 }).notNull(), // percentage (0-100)
  description: text("description"),
  isActive: boolean("is_active").default(true),
  effectiveDate: timestamp("effective_date"),
  endDate: timestamp("end_date"),
  createdAt: timestamp("created_at").defaultNow(),
});

// A general agency (GA) is a wholesaler a broker sends RFPs through instead of
// approaching each carrier directly. This is one agency's private list of them.
//
// Careful with the two "agency" words here, they mean opposite things:
//   agencyId  = the BROKER agency that owns this row
//   the row itself = a wholesaler that broker works with
//
// Not to be confused with ga_profiles (shared/schemas/ga.ts), which is the GA
// PORTAL's tenant: platform-wide general agencies that log in and are linked to
// brokers through ga_broker_links. A broker's GA may not be on the platform at
// all, which is why this is a separate, agency-scoped table.
export const generalAgencies = pgTable(
  "general_agencies",
  {
    id: serial("id").primaryKey(),
    agencyId: varchar("agency_id").notNull(), // broker agency that owns this record
    name: text("name").notNull(),
    website: text("website"),
    phone: text("phone"),
    notes: text("notes"),
    isActive: boolean("is_active").default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    // Every query filters on the owner.
    agencyIdIdx: index("general_agencies_agency_id_idx").on(table.agencyId),
  }),
);

export const directoryContacts = pgTable("directory_contacts", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id").notNull(), // broker user ID who owns this contact
  type: text("type").notNull(), // carrier | vendor
  name: text("name").notNull(),
  contactName: text("contact_name"),
  email: text("email").notNull(),
  phone: text("phone"),
  website: text("website"),
  address: text("address"),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  description: text("description"),
  category: text("category"),
  contactType: text("contact_type"), // sales | service
  carrierId: integer("carrier_id").references(() => carriers.id, { onDelete: "set null" }),
  // Which wholesaler this person works for, when type is "ga". NOT the owner —
  // that is agencyId above. Set null on delete, as carrierId is: removing a
  // general agency must never take its people with it.
  generalAgencyId: integer("general_agency_id").references(() => generalAgencies.id, {
    onDelete: "set null",
  }),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Organization reference tables
export const orgDepartments = pgTable("org_departments", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(), // agency | client | prospect
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  name: varchar("name", { length: 150 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_departments_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

export const orgJobTitles = pgTable("org_job_titles", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  name: varchar("name", { length: 150 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_job_titles_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

export const orgLocations = pgTable("org_locations", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  officeName: varchar("office_name", { length: 150 }),
  address1: varchar("address1", { length: 255 }),
  address2: varchar("address2", { length: 255 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  county: varchar("county", { length: 100 }),
  country: varchar("country", { length: 100 }).default("United States"),
  timeZone: varchar("time_zone", { length: 100 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_locations_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

export const orgEmployeeClasses = pgTable("org_employee_classes", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  name: varchar("name", { length: 150 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_employee_classes_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

// New: Divisions table
export const orgDivisions = pgTable("org_divisions", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(),
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  name: varchar("name", { length: 150 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  chkOwner: check("chk_org_divisions_owner", sql`(${t.ownerType} = 'agency' AND ${t.agencyId} IS NOT NULL AND ${t.clientId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'client' AND ${t.clientId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.prospectId} IS NULL) OR (${t.ownerType} = 'prospect' AND ${t.prospectId} IS NOT NULL AND ${t.agencyId} IS NULL AND ${t.clientId} IS NULL)`),
}));

// Company Admins table - users with full admin rights to a portal
export const orgAdmins = pgTable("org_admins", {
  id: serial("id").primaryKey(),
  ownerType: text("owner_type").notNull(), // 'agency', 'client', or 'prospect'
  agencyId: varchar("agency_id"),
  clientId: integer("client_id"),
  prospectId: integer("prospect_id"),
  userId: varchar("user_id", { length: 255 }).notNull(),
  isPrimaryAdmin: boolean("is_primary_admin").default(false).notNull(), // Primary admin cannot be removed without double authorization
  contactType: varchar("contact_type", { length: 50 }), // e.g., 'hr', 'accounting', 'benefits', 'payroll', 'general'
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schemas and types for org tables
export const insertOrgDepartmentSchema = createInsertSchema(
  orgDepartments
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertOrgJobTitleSchema = createInsertSchema(orgJobTitles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertOrgLocationSchema = createInsertSchema(orgLocations)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({ state: stateCodeSchema });
export const insertOrgEmployeeClassSchema = createInsertSchema(
  orgEmployeeClasses
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertOrgDivisionSchema = createInsertSchema(orgDivisions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Update (partial) schemas for org tables
export const updateOrgDepartmentSchema = insertOrgDepartmentSchema.partial();
export const updateOrgJobTitleSchema = insertOrgJobTitleSchema.partial();
export const updateOrgLocationSchema = insertOrgLocationSchema.partial();
export const updateOrgEmployeeClassSchema =
  insertOrgEmployeeClassSchema.partial();
export const updateOrgDivisionSchema = insertOrgDivisionSchema.partial();

export type OrgDepartment = typeof orgDepartments.$inferSelect;
export type InsertOrgDepartment = z.infer<typeof insertOrgDepartmentSchema>;
export type OrgJobTitle = typeof orgJobTitles.$inferSelect;
export type InsertOrgJobTitle = z.infer<typeof insertOrgJobTitleSchema>;
export type OrgLocation = typeof orgLocations.$inferSelect;
export type InsertOrgLocation = z.infer<typeof insertOrgLocationSchema>;
export type OrgEmployeeClass = typeof orgEmployeeClasses.$inferSelect;
export type InsertOrgEmployeeClass = z.infer<
  typeof insertOrgEmployeeClassSchema
>;
export type OrgDivision = typeof orgDivisions.$inferSelect;
export type InsertOrgDivision = z.infer<typeof insertOrgDivisionSchema>;
export type UpdateOrgDivision = z.infer<typeof updateOrgDivisionSchema>;
export type UpdateOrgDepartment = z.infer<typeof updateOrgDepartmentSchema>;
export type UpdateOrgJobTitle = z.infer<typeof updateOrgJobTitleSchema>;
export type UpdateOrgLocation = z.infer<typeof updateOrgLocationSchema>;
export type UpdateOrgEmployeeClass = z.infer<
  typeof updateOrgEmployeeClassSchema
>;

export const carriers = pgTable("carriers", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  types: text("types").array().notNull(), // array of: health, dental, vision, life, disability, voluntary
  wordBrownId: text("word_brown_id"),
  description: text("description"),
  isActive: boolean("is_active").default(true),
  // Carrier contacts are per agency and live in directory_contacts
  // (type "carrier", contactType "sales"), not here: this row is the shared
  // platform record, the same one for every agency.
  // State availability - array of US state codes (e.g., ["CA", "NY", "TX"])
  // NULL or empty means carrier is available in all states
  availableStates: text("available_states").array(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Which carriers a general agency can place business with. A GA does not hold
// appointments with every carrier, so an RFP sent through one must only ask it
// for the carriers it can actually approach.
//
// No agencyId of its own: both ends belong to one broker agency already, so
// scoping goes through general_agencies.
export const generalAgencyCarriers = pgTable(
  "general_agency_carriers",
  {
    id: serial("id").primaryKey(),
    generalAgencyId: integer("general_agency_id")
      .notNull()
      .references(() => generalAgencies.id, { onDelete: "cascade" }),
    carrierId: integer("carrier_id")
      .notNull()
      .references(() => carriers.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    // A link holds no data of its own, so losing either end makes the row
    // meaningless. Cascade, unlike directoryContacts which keeps the person.
    uniqueLink: uniqueIndex("general_agency_carriers_unique_idx").on(
      table.generalAgencyId,
      table.carrierId,
    ),
    carrierIdx: index("general_agency_carriers_carrier_idx").on(table.carrierId),
  }),
);

// Client Open Enrollment windows
export const clientOpenEnrollment = pgTable("client_open_enrollment", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull(),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  selectedPlanIds: integer("selected_plan_ids").array(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Employee-level Open Enrollment progress, per OE window start date
export const openEnrollmentProgress = pgTable(
  "open_enrollment_progress",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    employeeId: integer("employee_id").notNull(),
    oeStartDate: date("oe_start_date").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("not_started"), // not_started | pending | complete
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    byClientEmployeeCoverage: index("oep_client_employee_coverage").on(
      t.clientId,
      t.employeeId,
      t.oeStartDate
    ),
  })
);

// Insert schemas
export const insertSicCodeSchema = createInsertSchema(sicCodes).omit({
  id: true,
  createdAt: true,
});
export const insertClientSchema = createInsertSchema(clients)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    // A client must at least be identifiable — blank rows are unusable in
    // every list/detail surface. Enforced both in the Add/Edit Client forms
    // (zodResolver) and server-side (POST/PATCH parse this schema).
    name: z.string().trim().min(1, "Company name is required"),
    state: stateCodeSchema,
    // Arrives as text over JSON (the Add Client form defaults it to today).
    // createInsertSchema maps the timestamp column to z.date(), which rejects
    // any string — so every Add Client with a date filled in was refused.
    // Blank means "not set".
    clientSince: z.preprocess(
      (value) => (value === "" ? null : value),
      z.coerce.date().nullable().optional(),
    ),
    sicCodeId: z.number().optional().refine(
      (val) => val === undefined || val === null || SIC_CODES.some(sic => sic.code === val),
      { message: "Invalid SIC code. Must be a valid SIC code from the reference list." }
    ),
    renewalStage: z.enum(RENEWAL_STAGE_KEYS).optional(),
  });
export const insertRoleSchema = createInsertSchema(roles).omit({
  id: true,
  createdAt: true,
});
const baseProspectSchema = createInsertSchema(prospects).omit({
  id: true,
  createdAt: true,
});

export const insertProspectSchema = z.object({
  // Required fields
  name: z.string().min(1, "Company name is required"),
  contactEmail: z.string().email("Valid email is required"),
  contactPhone: z.string().min(1, "Contact phone is required"),
  // Optional fields with defaults from database
  status: z.string().optional(),
  priority: z.string().optional(),
  // Optional fields
  agencyId: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  address1: z.string().optional().nullable(),
  address2: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: stateCodeSchema,
  zipCode: z.string().optional().nullable(),
  county: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  source: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  expectedValue: z.union([z.string(), z.number()]).optional().nullable(),
  expectedCloseDate: z.union([z.string(), z.date()]).optional().nullable().or(z.literal(null)),
  lastContactDate: z.union([z.string(), z.date()]).optional().nullable().or(z.literal(null)),
  clientId: z.number().optional().nullable(),
  primaryProducerId: z.string().optional().nullable(),
  secondaryProducerId: z.string().optional().nullable(),
  accountManagerId: z.string().optional().nullable(),
  accountExecutiveId: z.string().optional().nullable(),
  teamId: z.number().optional().nullable(),
  logo: z.string().optional().nullable(),
  // Client-matching fields
  ein: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
  primaryContactName: z.string().optional().nullable(),
  entityType: z.string().optional().nullable(),
  sicCodeId: z.number().optional().nullable(),
  sic: z.string().optional().nullable(),
});
export const insertClientAccountManagerSchema = createInsertSchema(clientAccountManagers).omit({
  id: true,
  createdAt: true,
});
export const insertProspectAccountManagerSchema = createInsertSchema(prospectAccountManagers).omit({
  id: true,
  createdAt: true,
});
export const insertEmployeeDefaultSettingsSchema = createInsertSchema(
  employeeDefaultSettings,
  {
    defaults: employeeDefaultValuesSchema,
  }
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertEmployeeSchema = createInsertSchema(employees)
  .omit({ id: true, createdAt: true })
  .extend({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().min(1, "Work email is required").email("Invalid email address"),
    status: z.string().min(1, "Status is required"),
    employmentType: z.string().min(1, "Employment type is required"),
    // scheduledHours intentionally stays optional - don't add a rule here.
    hireDate: z.string().optional().or(z.date().optional()),
    dateOfBirth: z.string().optional().or(z.date().optional()),
    agencyId: z.string().optional().nullable(),
    departmentId: z.number().optional().nullable(),
    jobTitleId: z.number().optional().nullable(),
    locationId: z.number().optional().nullable(),
    employeeClassId: z.number().optional().nullable(),
    divisionId: z.number().optional().nullable(),
    state: stateCodeSchema,
    mailingState: stateCodeSchema,
  performanceRating: z.string().optional(),
  lastReviewDate: z.string().optional().or(z.date().optional()),
  skills: z.array(z.string()).optional(),
  managerEmployeeId: z.number().optional(),
  terminationDate: z.string().optional().or(z.date().optional()),
  });
export const insertDependentSchema = createInsertSchema(dependents)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    ssnFollowUpDate: z.string().optional().or(z.date().optional()),
    state: stateCodeSchema,
  });
export const insertEmergencyContactSchema = createInsertSchema(
  emergencyContacts
)
  .omit({ id: true, createdAt: true })
  .extend({ state: stateCodeSchema });
export const insertEmployeeHistorySchema = createInsertSchema(
  employeeHistory
).omit({ id: true, timestamp: true });

export const insertAgencyBenefitSchema = createInsertSchema(agencyBenefits)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    cost: z.number().min(0, "Cost must be non-negative"),
    participation: z
      .number()
      .min(0, "Participation must be non-negative")
      .max(100, "Participation cannot exceed 100%"),
  });
export const insertGeneralAgencySchema = createInsertSchema(generalAgencies)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z
      .string({ required_error: "The general agency needs a name" })
      .min(1, "The general agency needs a name"),
  });

export const insertDirectoryContactSchema = createInsertSchema(
  directoryContacts
)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    state: stateCodeSchema,
    // `name` is the ORGANISATION (NOT NULL), `contactName` the person. The two
    // read backwards, so say which one is missing.
    // required_error covers the field being absent, min(1) an empty string —
    // without the first, a missing `name` still answers a bare "Required".
    name: z
      .string({
        required_error:
          "Organization name is required — the person's name goes in contactName",
      })
      .min(1, "Organization name is required — the person's name goes in contactName"),
  });
export const insertCarrierSchema = createInsertSchema(carriers)
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    types: z.array(z.nativeEnum(CarrierType)).min(1, "At least one carrier type is required"),
    availableStates: z.array(z.string().length(2, "State codes must be 2 characters")).optional().nullable(),
  });

export const insertClientOpenEnrollmentSchema = createInsertSchema(
  clientOpenEnrollment
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertOpenEnrollmentProgressSchema = createInsertSchema(
  openEnrollmentProgress
).omit({ id: true, updatedAt: true });

// New Hire Enrollment Settings - configurable per agency (default) or client (override)
export const newHireEnrollmentSettings = pgTable("new_hire_enrollment_settings", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id"),      // Agency default (nullable)
  clientId: integer("client_id"),       // Client override (nullable)
  windowDays: integer("window_days").notNull().default(30), // Days from hire date to select plans
  isEnabled: boolean("is_enabled").notNull().default(true),
  reminderDays: integer("reminder_days").array().default([7, 3, 1]), // Days before deadline to send reminders
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// New Hire Enrollment Progress - tracks each employee's new hire enrollment status
export const newHireEnrollmentProgress = pgTable(
  "new_hire_enrollment_progress",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    employeeId: integer("employee_id").notNull(),
    windowStartDate: date("window_start_date").notNull(),  // = hire date
    windowEndDate: date("window_end_date").notNull(),      // = hire date + windowDays
    status: varchar("status", { length: 20 }).notNull().default("not_started"),
      // not_started | in_progress | completed | expired | waived
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    byClientEmployee: index("nhe_progress_client_employee").on(t.clientId, t.employeeId),
    byWindowEnd: index("nhe_progress_window_end").on(t.windowEndDate),
  })
);

export const insertNewHireEnrollmentSettingsSchema = createInsertSchema(
  newHireEnrollmentSettings
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertNewHireEnrollmentProgressSchema = createInsertSchema(
  newHireEnrollmentProgress
).omit({ id: true, createdAt: true, updatedAt: true });

// Time Off Policies - defines different types of time off (Vacation, Sick, Personal, etc.)
export const timeOffPolicies = pgTable("time_off_policies", {
  id: serial("id").primaryKey(),
  // Owner: exactly one of clientId / agencyId (DB check constraint). agencyId
  // holds the broker/agency user UUID so agencies can run their own HR.
  clientId: integer("client_id"),
  agencyId: varchar("agency_id", { length: 255 }),
  name: varchar("name", { length: 100 }).notNull(), // e.g., "Vacation", "Sick Leave", "Personal"
  code: varchar("code", { length: 20 }), // e.g., "VAC", "SICK", "PTO"
  policyType: varchar("policy_type", { length: 30 }).default("vacation"),
    // vacation | sick | personal | pto | fmla | bereavement | jury_duty | parental | military | unpaid | other
  description: text("description"),
  accrualType: varchar("accrual_type", { length: 20 }).notNull().default("annual"),
    // annual | monthly | bi_weekly | weekly | hourly | none
  accrualRate: decimal("accrual_rate", { precision: 10, scale: 4 }), // Hours/days accrued per period
  accrualUnit: varchar("accrual_unit", { length: 10 }).notNull().default("hours"), // hours | days
  maxBalance: decimal("max_balance", { precision: 10, scale: 2 }), // Maximum balance cap
  carryoverLimit: decimal("carryover_limit", { precision: 10, scale: 2 }), // Max hours/days that can carry over
  carryoverExpireMonths: integer("carryover_expire_months"), // Months until carryover expires
  waitingPeriodDays: integer("waiting_period_days").default(0), // Days before new hire can accrue
  allowNegativeBalance: boolean("allow_negative_balance").default(false),
  requiresApproval: boolean("requires_approval").default(true),
  minRequestIncrement: decimal("min_request_increment", { precision: 5, scale: 2 }).default("0.5"), // Minimum hours/days per request
  maxConsecutiveDays: integer("max_consecutive_days"), // Max consecutive days allowed
  advanceNoticeDays: integer("advance_notice_days").default(0), // Days notice required
  isActive: boolean("is_active").default(true),
  isPaid: boolean("is_paid").default(true),
  color: varchar("color", { length: 7 }), // Hex color for calendar display
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  byClient: index("time_off_policies_client").on(t.clientId),
  byClientActive: index("time_off_policies_client_active").on(t.clientId, t.isActive),
  byAgency: index("time_off_policies_agency").on(t.agencyId),
}));

// Time Off Balances - tracks employee balances per policy
export const timeOffBalances = pgTable("time_off_balances", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  policyId: integer("policy_id").notNull(),
  currentBalance: decimal("current_balance", { precision: 10, scale: 2 }).notNull().default("0"),
  usedBalance: decimal("used_balance", { precision: 10, scale: 2 }).notNull().default("0"),
  pendingBalance: decimal("pending_balance", { precision: 10, scale: 2 }).notNull().default("0"), // Approved but not yet taken
  carryoverBalance: decimal("carryover_balance", { precision: 10, scale: 2 }).default("0"),
  lastAccrualDate: date("last_accrual_date"),
  yearStartBalance: decimal("year_start_balance", { precision: 10, scale: 2 }).default("0"), // Balance at start of year
  year: integer("year").notNull(), // Calendar year for this balance record
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  byEmployee: index("time_off_balances_employee").on(t.employeeId),
  byPolicy: index("time_off_balances_policy").on(t.policyId),
  byEmployeeYear: index("time_off_balances_employee_year").on(t.employeeId, t.year),
  uniqueEmployeePolicyYear: uniqueIndex("time_off_balances_unique").on(t.employeeId, t.policyId, t.year),
}));

// Time Off Requests - individual time off requests
export const timeOffRequests = pgTable("time_off_requests", {
  id: serial("id").primaryKey(),
  employeeId: integer("employee_id").notNull(),
  policyId: integer("policy_id").notNull(),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  startTime: varchar("start_time", { length: 5 }), // HH:MM for partial day
  endTime: varchar("end_time", { length: 5 }), // HH:MM for partial day
  totalHours: decimal("total_hours", { precision: 10, scale: 2 }).notNull(),
  requestType: varchar("request_type", { length: 20 }).notNull().default("full_day"),
    // full_day | partial_day | multi_day
  status: varchar("status", { length: 20 }).notNull().default("pending"),
    // pending | approved | denied | cancelled | withdrawn
  reason: text("reason"),
  denialReason: text("denial_reason"),
  requestedAt: timestamp("requested_at").defaultNow(),
  reviewedAt: timestamp("reviewed_at"),
  reviewedBy: varchar("reviewed_by"), // User ID of approver
  notes: text("notes"), // Internal notes
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  byEmployee: index("time_off_requests_employee").on(t.employeeId),
  byPolicy: index("time_off_requests_policy").on(t.policyId),
  byStatus: index("time_off_requests_status").on(t.status),
  byDateRange: index("time_off_requests_dates").on(t.startDate, t.endDate),
  byEmployeeStatus: index("time_off_requests_employee_status").on(t.employeeId, t.status),
}));

// Time Off Approval Routes - configurable approval workflow per policy or client
export const timeOffApprovalRoutes = pgTable("time_off_approval_routes", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id"),
  agencyId: varchar("agency_id", { length: 255 }),
  policyId: integer("policy_id"), // Null means applies to all policies for client
  name: varchar("name", { length: 100 }).notNull(),
  approverType: varchar("approver_type", { length: 30 }).notNull(),
    // direct_manager | department_head | hr_manager | specific_user | any_admin
  approverUserId: varchar("approver_user_id"), // If specific_user
  approverEmployeeId: integer("approver_employee_id"), // If specific employee
  stepOrder: integer("step_order").notNull().default(1), // For multi-step approval
  isRequired: boolean("is_required").default(true),
  minDaysThreshold: integer("min_days_threshold"), // Only required if request >= X days
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  byClient: index("time_off_approval_routes_client").on(t.clientId),
  byPolicy: index("time_off_approval_routes_policy").on(t.policyId),
}));

// Time Off Settings - global settings for the Time Off module per client
export const timeOffSettings = pgTable("time_off_settings", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").unique(),
  agencyId: varchar("agency_id", { length: 255 }),
  accrualStartMonth: integer("accrual_start_month").default(1), // 1-12 for fiscal year start
  accrualStartDay: integer("accrual_start_day").default(1), // Day of month
  allowOverlappingRequests: boolean("allow_overlapping_requests").default(false),
  requireReasonForDenial: boolean("require_reason_for_denial").default(true),
  showBalanceToEmployees: boolean("show_balance_to_employees").default(true),
  allowEmployeeCancellation: boolean("allow_employee_cancellation").default(true),
  cancellationCutoffHours: integer("cancellation_cutoff_hours").default(24), // Hours before start
  defaultWorkHoursPerDay: decimal("default_work_hours_per_day", { precision: 4, scale: 2 }).default("8"),
  workWeekDays: jsonb("work_week_days").default(sql`'[1,2,3,4,5]'::jsonb`), // 0=Sun, 1=Mon, etc.
  holidayCalendarId: integer("holiday_calendar_id"), // Future: link to holiday calendar
  notifyManagerOnRequest: boolean("notify_manager_on_request").default(true),
  notifyEmployeeOnApproval: boolean("notify_employee_on_approval").default(true),
  notifyHrOnAllRequests: boolean("notify_hr_on_all_requests").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Benefits display settings - per-client toggles for what employees see on
// their benefit cards (e.g. the premium-cost breakdown popover).
export const benefitsDisplaySettings = pgTable("benefits_display_settings", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id").notNull().unique(),
  showPremiumBreakdownToEmployees: boolean("show_premium_breakdown_to_employees").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Time Off Audit Logs - tracks all time off changes for compliance
export const timeOffAuditLogs = pgTable("time_off_audit_logs", {
  id: serial("id").primaryKey(),
  clientId: integer("client_id"),
  agencyId: varchar("agency_id", { length: 255 }),
  entityType: varchar("entity_type", { length: 30 }).notNull(),
    // policy | balance | request | approval_route | settings
  entityId: integer("entity_id").notNull(),
  action: varchar("action", { length: 30 }).notNull(),
    // created | updated | deleted | approved | denied | cancelled | adjusted
  previousValues: jsonb("previous_values"),
  newValues: jsonb("new_values"),
  performedBy: varchar("performed_by").notNull(), // User ID
  performedByName: varchar("performed_by_name", { length: 200 }),
  ipAddress: varchar("ip_address", { length: 45 }),
  userAgent: text("user_agent"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  byClient: index("time_off_audit_logs_client").on(t.clientId),
  byEntity: index("time_off_audit_logs_entity").on(t.entityType, t.entityId),
  byPerformedBy: index("time_off_audit_logs_user").on(t.performedBy),
  byCreatedAt: index("time_off_audit_logs_date").on(t.createdAt),
}));

// Time Off Request Approvals - tracks individual approval steps for each request
export const timeOffRequestApprovals = pgTable("time_off_request_approvals", {
  id: serial("id").primaryKey(),
  requestId: integer("request_id").notNull(), // References timeOffRequests.id
  stepOrder: integer("step_order").notNull(), // 1, 2, 3... for multi-step approval
  approverType: varchar("approver_type", { length: 30 }).notNull(),
    // direct_manager | department_head | hr_manager | specific_user | any_admin
  approverEmployeeId: integer("approver_employee_id"), // The specific employee who should approve
  approverUserId: varchar("approver_user_id"), // The user ID who should approve
  status: varchar("status", { length: 20 }).notNull().default("pending"),
    // pending | approved | denied | skipped
  actedAt: timestamp("acted_at"),
  actedByUserId: varchar("acted_by_user_id"), // Who actually took action
  actedByEmployeeId: integer("acted_by_employee_id"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  byRequest: index("time_off_request_approvals_request").on(t.requestId),
  byRequestStep: index("time_off_request_approvals_request_step").on(t.requestId, t.stepOrder),
  byApprover: index("time_off_request_approvals_approver").on(t.approverEmployeeId),
  byStatus: index("time_off_request_approvals_status").on(t.status),
}));

// Insert schemas for Time Off module
export const insertTimeOffPolicySchema = createInsertSchema(timeOffPolicies)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertTimeOffBalanceSchema = createInsertSchema(timeOffBalances)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertTimeOffRequestSchema = createInsertSchema(timeOffRequests)
  .omit({ id: true, createdAt: true, updatedAt: true, requestedAt: true });

export const insertTimeOffApprovalRouteSchema = createInsertSchema(timeOffApprovalRoutes)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertTimeOffSettingsSchema = createInsertSchema(timeOffSettings)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertBenefitsDisplaySettingsSchema = createInsertSchema(benefitsDisplaySettings)
  .omit({ id: true, createdAt: true, updatedAt: true });

export const insertTimeOffAuditLogSchema = createInsertSchema(timeOffAuditLogs)
  .omit({ id: true, createdAt: true });

export const insertTimeOffRequestApprovalSchema = createInsertSchema(timeOffRequestApprovals)
  .omit({ id: true, createdAt: true });

// Types
export type Client = typeof clients.$inferSelect;
export type InsertClient = z.infer<typeof insertClientSchema>;
export type Prospect = typeof prospects.$inferSelect;
export type InsertProspect = z.infer<typeof insertProspectSchema>;
export type ClientAccountManager = typeof clientAccountManagers.$inferSelect;
export type InsertClientAccountManager = z.infer<typeof insertClientAccountManagerSchema>;
export type ProspectAccountManager = typeof prospectAccountManagers.$inferSelect;
export type InsertProspectAccountManager = z.infer<typeof insertProspectAccountManagerSchema>;
export type EmployeeDefaultSettings = typeof employeeDefaultSettings.$inferSelect;
export type InsertEmployeeDefaultSettings = z.infer<typeof insertEmployeeDefaultSettingsSchema>;
export type Employee = typeof employees.$inferSelect;
export type InsertEmployee = z.infer<typeof insertEmployeeSchema>;
export type Dependent = typeof dependents.$inferSelect;
export type InsertDependent = z.infer<typeof insertDependentSchema>;
export type EmergencyContact = typeof emergencyContacts.$inferSelect;
export type InsertEmergencyContact = z.infer<
  typeof insertEmergencyContactSchema
>;
export type EmployeeHistory = typeof employeeHistory.$inferSelect;
export type InsertEmployeeHistory = z.infer<typeof insertEmployeeHistorySchema>;
export type Carrier = typeof carriers.$inferSelect;
export type InsertCarrier = z.infer<typeof insertCarrierSchema>;
export type Role = typeof roles.$inferSelect;
export type InsertRole = z.infer<typeof insertRoleSchema>;
export type AgencyBenefit = typeof agencyBenefits.$inferSelect;
export type InsertAgencyBenefit = z.infer<typeof insertAgencyBenefitSchema>;
export type DirectoryContact = typeof directoryContacts.$inferSelect;
export type InsertDirectoryContact = z.infer<
  typeof insertDirectoryContactSchema
>;
export type GeneralAgency = typeof generalAgencies.$inferSelect;
export type InsertGeneralAgency = z.infer<typeof insertGeneralAgencySchema>;
export type GeneralAgencyCarrier = typeof generalAgencyCarriers.$inferSelect;
/** What the API returns: the firm plus the carriers it can place business with. */
export type GeneralAgencyWithCarriers = GeneralAgency & { carrierIds: number[] };
export type SicCode = typeof sicCodes.$inferSelect;
export type InsertSicCode = z.infer<typeof insertSicCodeSchema>;

export type ClientOpenEnrollment = typeof clientOpenEnrollment.$inferSelect;
export type InsertClientOpenEnrollment = z.infer<
  typeof insertClientOpenEnrollmentSchema
>;
export type OpenEnrollmentProgress = typeof openEnrollmentProgress.$inferSelect;
export type InsertOpenEnrollmentProgress = z.infer<
  typeof insertOpenEnrollmentProgressSchema
>;

export type NewHireEnrollmentSettings = typeof newHireEnrollmentSettings.$inferSelect;
export type InsertNewHireEnrollmentSettings = z.infer<
  typeof insertNewHireEnrollmentSettingsSchema
>;
export type NewHireEnrollmentProgress = typeof newHireEnrollmentProgress.$inferSelect;
export type InsertNewHireEnrollmentProgress = z.infer<
  typeof insertNewHireEnrollmentProgressSchema
>;

// Time Off Types
export type TimeOffPolicy = typeof timeOffPolicies.$inferSelect;
export type InsertTimeOffPolicy = z.infer<typeof insertTimeOffPolicySchema>;
export type TimeOffBalance = typeof timeOffBalances.$inferSelect;
export type InsertTimeOffBalance = z.infer<typeof insertTimeOffBalanceSchema>;
export type TimeOffRequest = typeof timeOffRequests.$inferSelect;
export type InsertTimeOffRequest = z.infer<typeof insertTimeOffRequestSchema>;
export type TimeOffApprovalRoute = typeof timeOffApprovalRoutes.$inferSelect;
export type InsertTimeOffApprovalRoute = z.infer<typeof insertTimeOffApprovalRouteSchema>;
export type TimeOffSettings = typeof timeOffSettings.$inferSelect;
export type InsertTimeOffSettings = z.infer<typeof insertTimeOffSettingsSchema>;
export type BenefitsDisplaySettings = typeof benefitsDisplaySettings.$inferSelect;
export type InsertBenefitsDisplaySettings = z.infer<typeof insertBenefitsDisplaySettingsSchema>;
export type TimeOffAuditLog = typeof timeOffAuditLogs.$inferSelect;
export type InsertTimeOffAuditLog = z.infer<typeof insertTimeOffAuditLogSchema>;
export type TimeOffRequestApproval = typeof timeOffRequestApprovals.$inferSelect;
export type InsertTimeOffRequestApproval = z.infer<typeof insertTimeOffRequestApprovalSchema>;

// ── Missing exports from dev branch ────────────────────────────────────────

export const insertOrgAdminSchema = createInsertSchema(orgAdmins).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateOrgAdminSchema = insertOrgAdminSchema.partial();
export type OrgAdmin = typeof orgAdmins.$inferSelect;
export type InsertOrgAdmin = z.infer<typeof insertOrgAdminSchema>;
export type UpdateOrgAdmin = z.infer<typeof updateOrgAdminSchema>;

// Company Holidays - paid holidays defined by employer (moved from legacy pto.ts)
export const companyHolidays = pgTable("company_holidays", {
  id: text("id").primaryKey(),
  clientId: integer("client_id"),
  agencyId: varchar("agency_id", { length: 255 }),
  name: text("name").notNull(),
  date: date("date").notNull(),
  recurring: boolean("recurring").default(true),
  paidHoliday: boolean("paid_holiday").default(true),
  description: text("description").default(""),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCompanyHolidaySchema = createInsertSchema(companyHolidays).extend({
  date: z.string(),
});
export type CompanyHoliday = typeof companyHolidays.$inferSelect;
export type InsertCompanyHoliday = z.infer<typeof insertCompanyHolidaySchema>;

